import {
    Box,
    Dialog,
    DialogContent,
    DialogTitle,
    Divider,
    IconButton,
    Typography,
} from '@mui/material';
import XMarkIcon from '@heroicons/react/24/solid/XMarkIcon';
import { FunctionComponent, useCallback, useEffect, useState } from 'react';
import { WidgetManifestEntry } from '@/models/widget';
import { GENERAL_DOCS } from '@/pages/widgetEditor/docs/generalDocs';
import { buildWidgetDoc } from '@/pages/widgetEditor/utils';
import { WidgetDocSection } from '@/pages/widgetEditor/components/WidgetDocSection';
import { DocCode } from '@/pages/widgetEditor/components/DocCode';

interface WidgetDocsDialogProps {
    open: boolean;
    onClose: () => void;
    widgets: WidgetManifestEntry[];
    /** Widget whose section starts expanded; `null` opens at the general part. */
    widgetId?: string | null;
    /** Absolute URL of the selected version's module entry. */
    entryUrl?: string;
}

/**
 * The full documentation: how embedding and this editor work, then one
 * expandable section per widget.
 *
 * Opened either from the documentation card (general first) or from a widget's
 * info button in the rail, which pre-expands that widget.
 */
export const WidgetDocsDialog: FunctionComponent<WidgetDocsDialogProps> = ({
    open,
    onClose,
    widgets,
    widgetId,
    entryUrl,
}) => {
    const [expandedId, setExpandedId] = useState<string | null>(
        widgetId ?? null
    );

    /*
     * Re-seeded on every open: the same dialog serves the general card and each
     * widget's info button, so which section is expanded depends on where it
     * was opened from, not on where it was left last time.
     */
    useEffect(() => {
        if (open) setExpandedId(widgetId ?? null);
    }, [open, widgetId]);

    /*
     * Scrolls the requested widget's section into view once it exists in the
     * document. A callback ref rather than a `useEffect` + `scrollIntoView`:
     * MUI's Dialog unmounts its `DialogContent` while closed (default
     * `keepMounted: false`) and remounts it fresh on every open, wrapped in
     * an enter transition and a portal — an effect keyed on `open` can fire
     * before that subtree has actually committed to the DOM, scrolling
     * against a stale or missing node. A callback ref instead runs exactly
     * when React attaches the target `Box` to the document, which is also
     * naturally "on every open" since the node remounts each time. Only
     * assigned to the section that matches `widgetId`, so it never fires for
     * the ten sections nobody asked to see.
     */
    const scrollRequestedSectionIntoView = useCallback(
        (node: HTMLDivElement | null) => {
            node?.scrollIntoView?.({ block: 'start' });
        },
        []
    );

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth='md'
            fullWidth
            scroll='paper'
        >
            <DialogTitle
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                }}
            >
                Dokumentation
                <IconButton
                    size='small'
                    aria-label='Dokumentation schließen'
                    onClick={onClose}
                >
                    <XMarkIcon style={{ width: 18, height: 18 }} />
                </IconButton>
            </DialogTitle>
            <Divider />
            <DialogContent>
                {GENERAL_DOCS.map((section) => (
                    <Box key={section.id} sx={{ mb: 3 }}>
                        <Typography variant='subtitle1' sx={{ mb: 1 }}>
                            {section.title}
                        </Typography>
                        <Typography variant='body2' color='text.secondary'>
                            {section.body}
                        </Typography>
                        {section.code && <DocCode>{section.code}</DocCode>}
                    </Box>
                ))}

                <Divider sx={{ my: 3 }} />

                <Typography variant='subtitle1' sx={{ mb: 2 }}>
                    Widgets im Detail
                </Typography>
                {widgets.length === 0 && (
                    <Typography variant='body2' color='text.secondary'>
                        Die Widget-Liste ist derzeit nicht verfügbar.
                    </Typography>
                )}
                {widgets.map((widget) => (
                    <Box
                        key={widget.widget}
                        ref={
                            widget.widget === widgetId
                                ? scrollRequestedSectionIntoView
                                : undefined
                        }
                    >
                        <WidgetDocSection
                            doc={buildWidgetDoc(widget, entryUrl)}
                            expanded={expandedId === widget.widget}
                            onToggle={(isExpanded) =>
                                setExpandedId(isExpanded ? widget.widget : null)
                            }
                        />
                    </Box>
                ))}
            </DialogContent>
        </Dialog>
    );
};
